import { MFAMethod } from '@/types/enums';
import { AxiosPromise } from 'axios';
import API from './axios-client';

type LoginType = { email: string; password: string };

type RegisterType = {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
};

type VerifyEmailType = { token: string; userId?: string };
type ForgotPasswordType = { email: string };
type ResetPasswordType = { password: string; resetPasswordToken: string };
type VerifyMFAType = { code: string; secretKey: string };
type MFALoginType = { code: string; email?: string };
type Email2FALoginType = { code: string; email?: string; method?: MFAMethod };
type SessionType = {
    id: string;
    userId: string;
    userAgent: string;
    createdAt: string;
    expiresAt: string;
    isCurrent: boolean;
    ip: string;
    city: string;
    country: string;
};

type SessionResponseType = {
    message: string;
    sessions: SessionType[];
};

type mfaType = {
    message: string;
    secret: string;
    qrImageUrl: string;
};

// ==============================================
// Auth API Routes

export const loginMutationFn = async (data: LoginType) =>
    await API.post('/auth/login', data);

export const registerMutationFn = async (data: RegisterType) =>
    await API.post('/auth/register', data);

export const verifyEmailMutationFn = async (data: VerifyEmailType) =>
    await API.post('/auth/email/verify', data);

export const forgotPasswordMutationFn = async (data: ForgotPasswordType) =>
    await API.post('/auth/password/forgot', data);

export const resetPasswordMutationFn = async (data: ResetPasswordType) =>
    await API.post('/auth/password/reset', data);

export const logoutMutationFn = async () => await API.post('/auth/logout');

export const deactivateAccountMutationFn = async (data: { userId: string }) =>
    await API.post('/auth/account/deactivate', data);

export const getUserAccessTokenQueryFn = async () =>
    await API.get('/auth/refresh');

// ==============================================
// MFA API Routes

// ==============================================
// Email MFA
export const mfaEmailVerifyLoginMutaionFn = async (data: Email2FALoginType) =>
    await API.post('/mfa/email/verify', data);

export const mfaEmailToggleMutationFn = async (userId: string) =>
    await API.post('/mfa/email/toggle', { userId });

export const mfaEmailSetupQueryFn = async (
    data: Pick<Email2FALoginType, 'email' | 'method'>,
) => {
    const response = await API.post('/mfa/email/setup', data);
    return response;
};

// ==============================================
// TOTP MFA
export const mfaTotpSetupQueryFn = async () => {
    const response = await API.get<mfaType>('/mfa/totp/setup');
    return response.data;
};

export const verifyMfaTotpMutationFn = async (data: VerifyMFAType) =>
    await API.post('/mfa/totp/setup/verify', { ...data });

export const revokeMfaTotpMutationFn = async () =>
    await API.put('/mfa/totp/revoke', {});

export const verifyMfaTotpLoginMutationFn = async (data: MFALoginType) =>
    await API.post('/mfa/totp/login/verify', data);

// Backup codes MFA

export const generateBackupCodesMutationFn = async (data: {
    userId: string;
    password: string;
}) => await API.post('/mfa/backupcodes/generate', { ...data });

export const destroyBackupCodesMutationFn = async (data: {
    userId: string;
    password: string;
}) => await API.post('/mfa/backupcodes/revoke', { ...data });

export const verifyBackupCodesMutationFn = async (data: {
    code: string;
    email?: string;
    methode?: string;
}) => await API.post('/mfa/backupcodes/login', { ...data });

// Recovery email MFA login

export const verifyRecoveryEmailForLoginMutationFn = async (data: {
    email?: string;
}) => await API.post('/mfa/recovery-email/login', { ...data });

// Magic link MFA
export const generateMagicLinkForLoginMutationFn = async (data: {
    email?: string;
}) => await API.post('/mfa/magic/generate', { ...data });

export const verifyMagicLinkForLoginMutationFn = async (data: {
    token?: string;
}) => await API.post('/mfa/magic/login', { ...data });

// ==============================================
// User API Routes
export const getUserProfileQueryFn = async () => await API.get(`/user/profile`);

export const deleteAccountMutationFn = async (data: {
    userId: string;
}): AxiosPromise<{ message: string }> =>
    await API.delete('/user/profile', { data });

export const updateUserProfileQueryFn = async (data: {
    name: string;
    userId: string;
}) => await API.post(`/user/profile/update`, data);

// Password
export const changePasswordMutationFn = async (data: {
    oldPassword: string;
    password: string;
    confirmPassword: string;
}) => await API.post('/user/profile/update/password', { ...data });

export const changeRecoveryEmailMutationFn = async (data: {
    email?: string;
    userId: string;
}) => await API.post('/user/recovery-email/change', { ...data });

// Email Change Routes
export const userEmailChangeRequestMutationFn = async (data: {
    newEmail?: string;
    userId: string;
    password: string;
}) => await API.post('/user/email-change/request', data);

export const userEmailChangeVerifyMutationFn = async (data: {
    token: string;
    userId?: string;
    password: string;
}) => await API.post('/user/email-change/verify', data);

export const userEmailChangeDeleteMutationFn = async (data: {
    userId?: string;
    requestId: string;
}) => await API.delete('/user/email-change', { data });

export const userEmailChangeQueryFn = async () =>
    await API.get('/user/email-change');

// ==============================================
// Session API Routes
export const sessionsQueryFn = async () => {
    const response = await API.get<SessionResponseType>('/session/all');
    return response.data;
};

export const revokeSessionMutationFn = async (id: string) =>
    await API.delete(`/session/${id}`);

export const revokeAllsessionsMutationFn = async () =>
    await API.delete('/session/all');
